import { useSyncExternalStore } from 'react'

// Phones get their own chrome where iOS and Android differ: the back button, where a title sits, how a sheet and a
// confirmation look, and where the main action floats. The page root carries data-platform, and the ios: and
// android: variants (index.css) style from it. Anything else (a computer, a tablet in desktop mode) gets the
// iOS-like default, which reads fine with a mouse.
export type Platform = 'ios' | 'android' | 'other'

export function detectPlatform(userAgent: string, touchPoints: number): Platform {
  if (/android/i.test(userAgent)) return 'android'
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios'
  // iPadOS asks for desktop pages and says it's a Mac; only the touch screen gives it away.
  if (/macintosh/i.test(userAgent) && touchPoints > 1) return 'ios'
  return 'other'
}

export function applyPlatform() {
  document.documentElement.dataset.platform = detectPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0)
}

export const platform = (): Platform => {
  const value = document.documentElement.dataset.platform
  return value === 'ios' || value === 'android' ? value : 'other'
}

// Below the nav breakpoint (foundation.css) the app wears its phone shell: tabs, sheets, a floating action.
const PHONE = '(max-width: 48.8125rem)'
const subscribe = (change: () => void) => {
  const query = matchMedia(PHONE)
  query.addEventListener('change', change)
  return () => query.removeEventListener('change', change)
}
export const useIsPhone = () => useSyncExternalStore(subscribe, () => matchMedia(PHONE).matches)

// From a sideways tablet up (64rem), an open sidebar sits beside the page; narrower, it slides over it.
const WIDE = '(min-width: 64rem)'
const subscribeWide = (change: () => void) => {
  const query = matchMedia(WIDE)
  query.addEventListener('change', change)
  return () => query.removeEventListener('change', change)
}
export const useIsWide = () => useSyncExternalStore(subscribeWide, () => matchMedia(WIDE).matches)
